import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { parseNeeds, useMe } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   Pieces every Creators page shares: the link helper, the permission
   helper and the hub's notice. The page frame is frame.tsx.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * A link to a Creators route.
 *
 * Module routes are built from the manifest at runtime, so the router's typed
 * path list only knows the core pages and rejects "/creators/...". The cast
 * lives here once instead of at every link.
 */
export function CLink({
  to,
  search,
  className,
  children,
  onClick,
  title,
  style,
  ...aria
}: {
  to: string;
  search?: Record<string, string | undefined>;
  className?: string;
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  title?: string;
  style?: CSSProperties;
  "aria-label"?: string;
  "aria-current"?: "page";
}) {
  return (
    <Link to={to as never} search={search as never} className={className} onClick={onClick} title={title} style={style} {...aria}>
      {children}
    </Link>
  );
}

/** The address of one creator's page. Names are the key, upper case throughout. */
export const creatorHref = (creator: string) => `/creators/${encodeURIComponent(creator.toUpperCase())}`;

/**
 * Held for every creator rather than for some. The house onboarding template
 * belongs to nobody in particular, so the server checks it without a record,
 * and a grant scoped to one creator does not reach it.
 */
export function useCanGlobally() {
  const { me } = useMe();
  return (needs: string) => {
    if (!me) return false;
    if (me.user.isOwner) return true;
    const [resource, verb] = parseNeeds(needs);
    return me.matrix[resource]?.includes(verb) ?? false;
  };
}

/** The message on an ApiError, or a fallback that still says what to do. */
export function messageOf(error: unknown, fallback = "Something went wrong. Try again in a moment."): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** A notice in the hub's style: a mark, then the sentence. */
export function Notice({
  tone = "",
  mark = "·",
  children,
  style,
  className = "",
}: {
  tone?: "" | "warn" | "bad" | "good";
  mark?: string;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div className={`notice${tone ? ` ${tone}` : ""}${className ? ` ${className}` : ""}`} style={style} role={tone === "bad" ? "alert" : undefined}>
      <i aria-hidden="true">{mark}</i>
      <span>{children}</span>
    </div>
  );
}
